import type {
  DayQualityCatalogService,
  DayQualityFrontView,
  DayQualityProjectFront,
} from '@obraplan/core/application/use-cases/quality/quality-use-cases';

/**
 * A frente do lançamento sem sinal (Fase 43, RN-618).
 *
 * Escolhida a atividade, o lançamento pede a frente inteira — os serviços, as fichas e o que já
 * foi verificado hoje —, uma leitura por atividade (`activityIds`). Sem sinal, a da atividade que
 * ninguém abriu antes não está no aparelho, e o lançamento ficava sem checklist.
 *
 * Os serviços e as fichas são os da **disciplina**: duas atividades da mesma disciplina têm os
 * mesmos, e só a verificação de hoje muda. Então a pré-carga lê uma frente de cada disciplina
 * (`representantesPorDisciplina`), e sem sinal a atividade escolhida usa a da disciplina dela,
 * sem verificação (`frentePelaDisciplina`). Quem verificou com sinal hoje já está na leitura.
 */

/**
 * Uma atividade de cada disciplina com serviço, em ordem de id: a leitura é a mesma na pré-carga
 * e na tela, e é isso que faz a tela achá-la no aparelho.
 */
export function representantesPorDisciplina(
  fronts: readonly DayQualityProjectFront[],
): readonly string[] {
  const porDisciplina = new Map<string, string>();
  for (const front of fronts) {
    if (!front.hasServices) continue;
    const atual = porDisciplina.get(front.disciplineName);
    if (atual === undefined || front.activityId < atual) {
      porDisciplina.set(front.disciplineName, front.activityId);
    }
  }
  return [...porDisciplina.values()].sort();
}

/**
 * O último recurso, quando nem a disciplina está no aparelho (ele nunca baixou o dia com sinal):
 * os serviços do catálogo, sem ficha. O lançamento não fica esperando uma checklist que não vem.
 * Só horas sobem; a quantidade num serviço com ficha o servidor recusa (RN-218), e o lançamento
 * fica para revisar (D3).
 */
export function frenteSemFicha(
  activityId: string,
  fronts: readonly DayQualityProjectFront[],
  catalogo: readonly DayQualityCatalogService[],
): DayQualityFrontView | undefined {
  const frente = fronts.find((item) => item.activityId === activityId);
  if (frente === undefined) return undefined;
  return {
    activityId,
    label: frente.label,
    disciplineName: frente.disciplineName,
    services: catalogo
      .filter((service) => service.disciplineName === frente.disciplineName)
      .map((service) => ({ serviceId: service.serviceId, name: service.name, checklists: [] })),
  };
}

/** Quantas atividades a leitura da qualidade aceita de uma vez (`dayQualitySchema`). */
export const ATIVIDADES_POR_LEITURA = 20;

/** As representantes em lotes que a leitura aceita, sempre os mesmos para a mesma obra. */
export function lotesPorDisciplina(
  fronts: readonly DayQualityProjectFront[],
): readonly (readonly string[])[] {
  const todas = representantesPorDisciplina(fronts);
  const lotes: string[][] = [];
  for (let inicio = 0; inicio < todas.length; inicio += ATIVIDADES_POR_LEITURA) {
    lotes.push(todas.slice(inicio, inicio + ATIVIDADES_POR_LEITURA));
  }
  return lotes;
}

/** A frente da atividade com os serviços e as fichas de outra da mesma disciplina. */
export function frentePelaDisciplina(
  activityId: string,
  fronts: readonly DayQualityProjectFront[],
  lidas: readonly DayQualityFrontView[],
): DayQualityFrontView | undefined {
  const frente = fronts.find((item) => item.activityId === activityId);
  if (frente === undefined) return undefined;
  const modelo = lidas.find((item) => item.disciplineName === frente.disciplineName);
  if (modelo === undefined) return undefined;
  return {
    activityId,
    label: frente.label,
    disciplineName: frente.disciplineName,
    // A verificação é da outra atividade: aqui ninguém verificou.
    services: modelo.services.map(({ verification: _outra, ...service }) => service),
  };
}
